import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import i18n from "@/i18n";
import { Sparkles, Loader2, Wand2, UserCheck, AlertTriangle } from "lucide-react";

export interface ParsedField {
  value: string | number;
  confidence: number;
  label?: string;
}

export interface ParseResult {
  fields: Record<string, ParsedField>;
  draft: string;
  leadScore: number;
  overallConfidence: number;
  matchedCustomer: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  matchedLead: { id: string; leadNumber: string; status: string } | null;
}

interface AiReviewPanelProps {
  /** Called when the user accepts the parse — fills the form. */
  onApply: (result: ParseResult) => void;
}

const FIELD_LABELS: Record<string, string> = {
  contactName: i18n.t("common.fields.name"),
  contactEmail: i18n.t("common.fields.email"),
  contactPhone: i18n.t("common.fields.phone"),
  destinationId: i18n.t("common.fields.destination"),
  tripId: i18n.t("common.fields.trip"),
  requestedDate: i18n.t("common.fields.date"),
  paxAdults: i18n.t("shared.aiReview.fields.adults"),
  paxChildren: i18n.t("shared.aiReview.fields.children"),
  notes: i18n.t("shared.aiReview.fields.summary"),
};

function confColor(c: number): string {
  if (c >= 0.8) return "bg-green-500";
  if (c >= 0.5) return "bg-amber-500";
  return "bg-gray-300 dark:bg-gray-600";
}

/**
 * AI first-reaction review (vision §7 / §5.2). Paste an inquiry (email / DM / call
 * notes) → the model extracts structured fields with per-field confidence, matches
 * the catalog + an existing customer (dedup), scores the lead, and drafts a reply.
 * Read-only until the agent clicks Apply — nothing is written without review.
 */
export default function AiReviewPanel({ onApply }: AiReviewPanelProps) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [result, setResult] = useState<ParseResult | null>(null);

  const parse = useMutation({
    mutationFn: (input: string) =>
      api.post<ApiResponse<ParseResult>>("/api/admin/ai/parse-lead", { text: input }),
    onSuccess: (res) => setResult(res.data),
  });

  return (
    <div className="card border-primary-200 dark:border-primary-900/40 border">
      <div className="mb-3 flex items-center gap-2">
        <Sparkles className="text-primary-600 dark:text-primary-400 h-5 w-5" />
        <h2 className="font-semibold text-gray-900 dark:text-white">
          {t("shared.aiReview.title")}
        </h2>
        <span className="text-xs text-gray-400">{t("shared.aiReview.subtitle")}</span>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        placeholder={t("shared.aiReview.inputPlaceholder")}
        className="input resize-none text-sm"
      />

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={() => parse.mutate(text)}
          disabled={text.trim().length < 3 || parse.isPending}
          className="btn-primary btn-sm disabled:opacity-50"
        >
          {parse.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Wand2 className="h-4 w-4" />
          )}
          {t("shared.aiReview.parseButton")}
        </button>
      </div>

      {parse.isError && (
        <p className="mt-2 text-sm text-red-600">{t("shared.aiReview.parseFailed")}</p>
      )}

      {result && (
        <div className="mt-4 space-y-3 border-t border-gray-100 pt-4 dark:border-gray-700/60">
          {/* Score + dedup alerts */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              <Sparkles className="h-3 w-3" />{" "}
              {t("shared.aiReview.score", { score: result.leadScore })}
            </span>
            <span className="text-xs text-gray-400">
              {t("shared.aiReview.avgConfidence", {
                percent: Math.round(result.overallConfidence * 100),
              })}
            </span>
          </div>

          {result.matchedCustomer && (
            <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
              <UserCheck className="h-4 w-4 shrink-0" />
              {t("shared.aiReview.returningCustomer")}{" "}
              {[result.matchedCustomer.firstName, result.matchedCustomer.lastName]
                .filter(Boolean)
                .join(" ") || result.matchedCustomer.email}
            </div>
          )}
          {result.matchedLead && (
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {t("shared.aiReview.existingLead", {
                leadNumber: result.matchedLead.leadNumber,
                status: result.matchedLead.status,
              })}
            </div>
          )}

          {/* Extracted fields with confidence */}
          <div className="grid gap-1.5 sm:grid-cols-2">
            {Object.entries(result.fields).map(([key, f]) => {
              const display = f.label || (f.value === "" ? "—" : String(f.value));
              return (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${confColor(f.confidence)}`} />
                  <span className="w-20 shrink-0 text-gray-500">{FIELD_LABELS[key] ?? key}</span>
                  <span className="truncate text-gray-900 dark:text-white">{display}</span>
                </div>
              );
            })}
          </div>

          {/* Draft reply */}
          {result.draft && (
            <div>
              <p className="mb-1 text-xs font-medium text-gray-500">
                {t("shared.aiReview.suggestedReply")}
              </p>
              <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                {result.draft}
              </p>
            </div>
          )}

          <div className="flex justify-end">
            <button type="button" onClick={() => onApply(result)} className="btn-primary btn-sm">
              <Wand2 className="h-4 w-4" />
              {t("shared.aiReview.applyToForm")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
